import type { NotificationType as PrismaNotificationType } from "../../../service/prisma/client.ts";

export const VISIBILITY_DURATION_PERSONALS_MONTH = 3; // personal notifications are visible for 3 months.
export const VISIBILITY_DURATION_BROADCASTS_MONTH = 3; // broadcasts are visible for 3 months.
export const PURGE_LEEWAY_MONTH = 12; // keep invisible notifications for a few more months before purging

/**
 * Calculate a date N months in the past from the given reference date.
 * Uses setMonth which handles year boundaries correctly.
 */
export function monthsAgo(months: number, from: Date = new Date()): Date {
  const d = new Date(from);
  d.setMonth(d.getMonth() - months);
  return d;
}

/** Cut-off date for personal notification visibility */
export function personalVisibilityCutDate(from?: Date): Date {
  return monthsAgo(VISIBILITY_DURATION_PERSONALS_MONTH, from);
}

/**
 * Marker the server puts on every push deep link (`notify.ts`
 * `notificationLink`) and the client reads back off the URL
 * (`NotificationRouter`) to attribute a PUSH OPEN.
 *
 * It exists because arriving on `/profile/notifications/<id>` is not by itself
 * evidence of a push: the legacy `clickedAt` counts any arrival there. The marker
 * is what makes `pushOpenedAt` attributable. Shared so the two sides of that
 * contract cannot drift — a mismatch would silently zero the push-open rate
 * rather than fail.
 */
export const PUSH_ENTRY_PARAM = "from";
export const PUSH_ENTRY_VALUE = "push";

/** Cut-off date for broadcast notification visibility */
export function broadcastVisibilityCutDate(from?: Date): Date {
  return monthsAgo(VISIBILITY_DURATION_BROADCASTS_MONTH, from);
}

/** Cut-off for broadcast visibility, but not before the user was created */
export function broadcastVisibilityCutDateForUser(
  userCreationDate: Date | undefined,
  from?: Date,
): Date {
  const broadcastsCutoff = broadcastVisibilityCutDate(from);
  if (userCreationDate === undefined) return broadcastsCutoff;
  return new Date(
    Math.max(userCreationDate.getTime(), broadcastsCutoff.getTime()),
  );
}

/** Cut-off date for purging personal notifications (visibility + leeway) */
export function personalPurgeCutDate(from?: Date): Date {
  return monthsAgo(
    VISIBILITY_DURATION_PERSONALS_MONTH + PURGE_LEEWAY_MONTH,
    from,
  );
}

/** Cut-off date for purging broadcast interactions (visibility + leeway) */
export function broadcastInteractionPurgeCutDate(from?: Date): Date {
  return monthsAgo(
    VISIBILITY_DURATION_BROADCASTS_MONTH + PURGE_LEEWAY_MONTH,
    from,
  );
}

/** Re-exported from @prisma/client as type alias */
export type NotificationType = PrismaNotificationType;

export const NotificationType: { [K in NotificationType]: K } = {
  Broadcast: "Broadcast",
  Workflow: "Workflow",
  VisualAbstractReady: "VisualAbstractReady",
  ExternalLink: "ExternalLink",
  ContentRecommended: "ContentRecommended",
  ContentNew: "ContentNew",
  VideoPublished: "VideoPublished",
  ArticlePublished: "ArticlePublished",
  LemonCertifiedAnswered: "LemonCertifiedAnswered",
  PlaylistShared: "PlaylistShared",
  TestNotification: "TestNotification",
  FollowRequestSent: "FollowRequestSent",
  FollowRequestReceived: "FollowRequestReceived",
  FollowRequestAccepted: "FollowRequestAccepted",
  FollowUnfollow: "FollowUnfollow",
  FollowActivity: "FollowActivity",
  ContentShare: "ContentShare",
  ConnectionRequest: "ConnectionRequest",
  ConnectionRequestAccepted: "ConnectionRequestAccepted",
  DirectMessage: "DirectMessage",
};

export const BROADCAST_TYPES = [
  "Broadcast",
  "ExternalLink",
  "ContentNew",
  "ContentRecommended",
] as const satisfies NotificationType[];

export const PERSONAL_TYPES = [
  "VideoPublished",
  "ArticlePublished",
  "PlaylistShared",
  "TestNotification",
  // Workflow pushes are personal notifications and MUST be listed here: every
  // personals endpoint filters on this set, and a push whose type is absent
  // 404s on resolution — the tap then dead-ends on the notification list
  // instead of the workflow's deep link, and no open is ever recorded.
  "Workflow",
  // A poster whose background generation finished. Personal by construction: a
  // visual abstract belongs to the one user who asked for it.
  "VisualAbstractReady",
] as const satisfies NotificationType[];

export type BroadcastNotificationType = (typeof BROADCAST_TYPES)[number];
export type PersonalNotificationType =
  (typeof PERSONAL_TYPES)[number] | "LemonCertifiedAnswered";
export type PersonalAction =
  | { notificationType: "VideoPublished"; content: string }
  | { notificationType: "ArticlePublished"; article: string; journal: string }
  | { notificationType: "PlaylistShared"; playlist: string }
  | { notificationType: "LemonCertifiedAnswered" }
  | { notificationType: "TestNotification" }
  // The visual abstract whose generation just settled as `ready`. The id is what
  // the notification centre row navigates to — the poster's preview — so the
  // record stays actionable long after the toast that announced it is gone.
  | { notificationType: "VisualAbstractReady"; visualAbstract: string }
  // A push emitted by a workflow's `send-push` action. `workflow` is the
  // workflow name, so the notification-center record is self-describing for
  // per-(user, workflow) auditing. `conversion` is what the push was driving,
  // recorded at send time so "did this step work" is answerable later — null for
  // a push with no content target (a feature intro, a credits nudge).
  | {
      notificationType: "Workflow";
      workflow: string;
      // The emitting step, so the funnel is per step rather than per workflow —
      // a content push and a profile nudge are not the same question.
      step: string;
      conversion: PushConversionTarget | null;
    };

/**
 * The content a push was driving the user towards.
 *
 * Recorded at send time because it is unrecoverable afterwards: the push link is
 * a URL, and a slug is not the `contentId` that `user_content_events` records. It
 * is what turns "opened" into "converted" — see docs/messaging-and-workflows.md.
 */
export type PushConversionTarget = {
  kind: "article" | "content" | "playlist";
  id: string;
};
